/* eslint-disable @typescript-eslint/no-unsafe-return */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import { PARTNERS_QUERY_KEY } from '~/api/query-keys/partners';
import type {
  CreateQuoteRequestRequest,
  GetMyQuoteRequestsParams,
  GetPartnersParams,
  GetRecommendedPartnersParams,
} from '~/api/repository/partners';
import { partnersRepository } from '~/api/repository/partners';

// Re-export types for convenience
// Request 타입 및 Params
export type {
  CreateQuoteRequestRequest,
  GetMyQuoteRequestsParams,
  GetPartnersParams,
  GetRecommendedPartnersParams,
} from '~/api/repository/partners';

// Response 타입 (Model)
export type {
  AcceptQuoteResponse,
  CreateQuoteResponse,
  MyQuoteRequestItem,
  MyQuoteRequestList,
  PartnerCardItem,
  PartnerDetail,
  PartnerGrade,
  PartnerList,
  QuoteRequestDetail,
  QuoteRequestSummary,
  ReceivedQuoteItem,
  RecommendedPartnerList,
} from '~/api/repository/partners';

// ============================================================================
// Query 훅
// ============================================================================

/** 파트너 목록 조회 */
// GET /api/market-place/partners
export const usePartners = (params: GetPartnersParams) => {
  const { queryKey, queryFn } = PARTNERS_QUERY_KEY.list(params);

  return useQuery(queryKey, queryFn, {
    suspense: true,
  });
};

/** 파트너 목록 무한 스크롤 조회 */
// GET /api/market-place/partners
export const useInfinitePartners = (
  params: Omit<GetPartnersParams, 'lastId'>,
) => {
  const { queryKey, queryFn } = PARTNERS_QUERY_KEY.infiniteList(params);

  return useInfiniteQuery(
    queryKey,
    ({ pageParam }) => queryFn(pageParam as number | undefined),
    {
      getNextPageParam: (lastPage) => {
        // hasNextPage가 true이면 다음 페이지 존재
        if (lastPage.hasNextPage) {
          return lastPage.lastId;
        }
        return undefined;
      },
      suspense: true,
    },
  );
};

/** 추천 파트너 목록 조회 (지역 기반) */
// GET /api/market-place/partners/recommended
export const useRecommendedPartners = (
  params: GetRecommendedPartnersParams,
) => {
  const { queryKey, queryFn } = PARTNERS_QUERY_KEY.recommended(params);

  return useQuery(queryKey, queryFn, {
    suspense: true,
    enabled: !!params.regionCode,
  });
};

/** 파트너 상세 조회 */
// GET /api/market-place/partners/{partnerId}
export const usePartnerDetail = (partnerId: number) => {
  const { queryKey, queryFn } = PARTNERS_QUERY_KEY.detail(partnerId);

  return useQuery(queryKey, queryFn, {
    suspense: true,
    enabled: !!partnerId,
  });
};

/** 내 견적 요청 목록 조회 */
// GET /api/market-place/partners/my/quote-requests
export const useMyQuoteRequests = (params: GetMyQuoteRequestsParams) => {
  const { queryKey, queryFn } = PARTNERS_QUERY_KEY.myQuoteRequests(params);

  return useQuery(queryKey, queryFn, {
    suspense: true,
  });
};

/** 견적 요청 상세 조회 */
// GET /api/market-place/partners/my/quote-requests/{quoteRequestId}
export const useQuoteRequestDetail = (quoteRequestId: number) => {
  const { queryKey, queryFn } =
    PARTNERS_QUERY_KEY.quoteRequestDetail(quoteRequestId);

  return useQuery(queryKey, queryFn, {
    suspense: true,
    enabled: !!quoteRequestId,
  });
};

/** 견적 요청 요약 조회 (누적 건수) */
// GET /api/market-place/partners/quote-requests/summary
export const useQuoteRequestSummary = () => {
  const { queryKey, queryFn } = PARTNERS_QUERY_KEY.quoteRequestSummary();

  return useQuery(queryKey, queryFn, {
    suspense: true,
  });
};

// ============================================================================
// Mutation 훅
// ============================================================================

/** 견적 요청 생성 */
// POST /api/market-place/partners/quote-requests
export const useCreateQuoteRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateQuoteRequestRequest) =>
      partnersRepository.createQuoteRequest(request),
    onSuccess: () => {
      void queryClient.invalidateQueries(PARTNERS_QUERY_KEY.all);
    },
  });
};

/** 견적 수락 */
// POST /api/market-place/partners/my/quote-responses/{quoteResponseId}/accept
export const useAcceptQuote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (quoteResponseId: number) =>
      partnersRepository.acceptQuote(quoteResponseId),
    onSuccess: () => {
      void queryClient.invalidateQueries(PARTNERS_QUERY_KEY.all);
    },
  });
};

/** 견적 거절 */
// POST /api/market-place/partners/my/quote-responses/{quoteResponseId}/reject
export const useRejectQuote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (quoteResponseId: number) =>
      partnersRepository.rejectQuote(quoteResponseId),
    onSuccess: () => {
      void queryClient.invalidateQueries(PARTNERS_QUERY_KEY.all);
    },
  });
};
